import React, { useEffect, useState } from "react";
import { ShieldCheck, Droplets, Sparkles, Truck, Wrench, Gem, Recycle, Heart, Quote, MapPin, Mail, MessageCircle, Star } from "lucide-react";
import Header from "@/components/Header";
import CartDrawer from "@/components/CartDrawer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { STRINGS } from "@/i18n";
import client from "@/lib/apiClient";
import { BRAND } from "@/config";

const HERO_IMG = "https://images.pexels.com/photos/3019302/pexels-photo-3019302.jpeg";
const EXP_IMG = "https://images.pexels.com/photos/15930026/pexels-photo-15930026.jpeg";

const SERVICE_ICONS = [Gem, Sparkles, Truck, Wrench];
const PROBLEM_ICONS = [Droplets, Heart, Recycle, ShieldCheck];

const CATEGORY_ORDER = ["chains", "bracelets", "charms", "earrings", "other"];

function catKey(cat = "") {
  const c = cat.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  if (c.startsWith("cadena") || c.startsWith("collar") || c.startsWith("chain") || c.startsWith("necklace")) return "chains";
  if (c.startsWith("pulsera") || c.startsWith("bracelet")) return "bracelets";
  if (c.startsWith("dije") || c.startsWith("charm")) return "charms";
  if (c.startsWith("pendiente") || c.startsWith("arete") || c.startsWith("earring")) return "earrings";
  return "other";
}

function Reveal({ children, delay = 0, className = "" }) {
  return <div className={`animate-fade-up ${className}`} style={{ animationDelay: `${delay}s` }}>{children}</div>;
}

function ProductCard({ p, lang, t }) {
  const { addItem } = useCart();
  const name = lang === "en" && p.name_en ? p.name_en : p.name;
  const category = lang === "en" && p.category_en ? p.category_en : p.category;
  return (
    <div data-testid={`product-card-${p.id}`} className="group">
      <div className="relative overflow-hidden rounded-lg bg-secondary aspect-[4/5] border border-white/10">
        <img src={p.image_url} alt={name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        {!p.in_stock && <span className="absolute top-3 left-3 text-xs uppercase tracking-wide bg-black/70 px-2 py-1 rounded">{t.catalog.soldOut}</span>}
        <div className="absolute inset-x-0 bottom-0 p-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
          <Button
            data-testid={`add-to-cart-${p.id}`}
            disabled={!p.in_stock}
            onClick={() => addItem(p, name)}
            className="w-full bg-primary text-primary-foreground hover:bg-white/90 uppercase tracking-wide text-xs h-10"
          >
            {t.catalog.add}
          </Button>
        </div>
      </div>
      <div className="mt-4 flex items-start justify-between gap-3">
        <div>
          <p className="text-overline mb-1">{category}</p>
          <h3 className="font-display text-lg leading-tight">{name}</h3>
        </div>
        <span className="font-mono text-base whitespace-nowrap">${p.price.toFixed(2)}</span>
      </div>
    </div>
  );
}

export default function Storefront() {
  const { lang } = useLang();
  const t = STRINGS[lang];
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hero, setHero] = useState(HERO_IMG);
  const [expImg, setExpImg] = useState(EXP_IMG);

  useEffect(() => {
    client.get("/products").then((r) => setProducts(r.data)).catch(() => {}).finally(() => setLoading(false));
    client.get("/settings").then((r) => {
      if (r.data?.hero_image) setHero(r.data.hero_image);
      if (r.data?.experience_image) setExpImg(r.data.experience_image);
    }).catch(() => {});
  }, []);

  return (
    <div id="top" className="bg-background text-foreground">
      <Header />
      <CartDrawer />
      <WhatsAppFloat />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center">
        <img src={hero} alt="Valientes Cenizas Shop" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/20" />
        <div className="relative px-6 md:px-12 lg:px-24 max-w-3xl py-32">
          <Reveal><p className="text-overline mb-5">{t.hero.overline}</p></Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-none tracking-tight">
              Valientes Cenizas <span className="italic text-muted-foreground">Shop</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-base md:text-lg text-muted-foreground font-light max-w-xl leading-relaxed">
              {t.hero.subtitle}
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-9 flex flex-wrap gap-4">
              <a href="#catalogo"><Button data-testid="hero-shop-button" className="h-12 px-8 bg-primary text-primary-foreground hover:bg-white/90 uppercase tracking-wide">{t.hero.cta}</Button></a>
              <a href={`https://wa.me/${BRAND.whatsapp}`} target="_blank" rel="noreferrer">
                <Button data-testid="hero-whatsapp-button" variant="outline" className="h-12 px-8 border-white/30 bg-transparent hover:bg-white/10 uppercase tracking-wide gap-2">
                  <MessageCircle className="h-4 w-4" /> {t.hero.contact}
                </Button>
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-white/10 py-4 overflow-hidden whitespace-nowrap bg-secondary/40">
        <div className="inline-flex animate-marquee">
          {[0, 1].map((k) => (
            <span key={k} className="font-display text-sm tracking-[0.2em] uppercase text-muted-foreground">
              {Array(6).fill(t.marquee).map((txt, i) => <span key={i} className="mx-2">{txt}</span>)}
            </span>
          ))}
        </div>
      </div>

      {/* CATALOG */}
      <section id="catalogo" className="px-6 md:px-12 lg:px-24 py-20 md:py-32">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <p className="text-overline mb-3">{t.catalog.overline}</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight">{t.catalog.title}</h2>
          </div>
          <p className="text-sm text-muted-foreground max-w-xs">{t.catalog.desc}</p>
        </div>
        {loading ? (
          <p className="text-muted-foreground font-mono text-sm">{t.catalog.loading}</p>
        ) : (
          (() => {
            const groups = CATEGORY_ORDER
              .map((key) => ({ key, items: products.filter((p) => catKey(p.category) === key) }))
              .filter((g) => g.items.length > 0);
            return (
              <>
                <div data-testid="category-nav" className="flex flex-wrap gap-3 mb-14">
                  {groups.map((g) => (
                    <a key={g.key} href={`#cat-${g.key}`}>
                      <Button data-testid={`cat-nav-${g.key}`} variant="outline" className="border-white/20 bg-transparent hover:bg-white/10 uppercase tracking-wide text-xs h-10 px-6">
                        {t.catalog.sections[g.key]}
                      </Button>
                    </a>
                  ))}
                </div>
                <div className="space-y-20">
                  {groups.map((g) => (
                    <div key={g.key} id={`cat-${g.key}`} data-testid={`category-section-${g.key}`} className="scroll-mt-24">
                      <div className="flex items-center gap-5 mb-8">
                        <h3 className="font-display text-xl sm:text-2xl tracking-tight">{t.catalog.sections[g.key]}</h3>
                        <span className="h-px flex-1 bg-white/10" />
                        <span className="text-xs font-mono text-muted-foreground">{g.items.length}</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                        {g.items.map((p) => <ProductCard key={p.id} p={p} lang={lang} t={t} />)}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            );
          })()
        )}
      </section>

      {/* SERVICES */}
      <section id="servicios" className="px-6 md:px-12 lg:px-24 py-20 md:py-32 bg-secondary/30 border-y border-white/10">
        <p className="text-overline mb-3">{t.services.overline}</p>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight mb-12 max-w-2xl">{t.services.title}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {t.services.items.map((s, i) => {
            const Icon = SERVICE_ICONS[i];
            return (
              <div key={i} data-testid={`service-${i}`} className="bg-card border border-white/10 rounded-lg p-7 hover:-translate-y-1 transition-transform duration-300">
                <Icon className="h-7 w-7 mb-5 text-foreground" strokeWidth={1.5} />
                <h3 className="font-display text-xl mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed font-light">{s.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* EXPERIENCE */}
      <section id="experiencia" className="px-6 md:px-12 lg:px-24 py-20 md:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="relative">
            <img src={expImg} alt={t.experience.title} className="rounded-lg w-full object-cover aspect-[4/3] border border-white/10" />
          </div>
          <div>
            <p className="text-overline mb-3">{t.experience.overline}</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight mb-6">{t.experience.title}</h2>
            <p className="text-muted-foreground leading-relaxed font-light mb-8">{t.experience.text}</p>
            <div className="grid grid-cols-3 gap-6">
              {[["100%", t.experience.stats[0]], ["+500", t.experience.stats[1]], ["5★", t.experience.stats[2]]].map(([n, l], i) => (
                <div key={i} data-testid={`stat-${i}`}>
                  <p className="font-display text-3xl md:text-4xl">{n}</p>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mt-1">{l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SOLUCIONES */}
      <section id="soluciones" className="px-6 md:px-12 lg:px-24 py-20 md:py-32 bg-secondary/30 border-y border-white/10">
        <p className="text-overline mb-3">{t.solutions.overline}</p>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight mb-4 max-w-2xl">{t.solutions.title}</h2>
        <p className="text-muted-foreground font-light mb-12 max-w-xl">{t.solutions.intro}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {t.solutions.items.map((p, i) => {
            const Icon = PROBLEM_ICONS[i];
            return (
              <div key={i} data-testid={`solution-${i}`} className="flex gap-5 bg-card border border-white/10 rounded-lg p-7">
                <div className="shrink-0 h-12 w-12 rounded-full bg-secondary flex items-center justify-center">
                  <Icon className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground line-through decoration-destructive/60 mb-1">{p.problem}</p>
                  <p className="font-display text-lg leading-snug">{p.solution}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonios" className="px-6 md:px-12 lg:px-24 py-20 md:py-32">
        <p className="text-overline mb-3">{t.testimonials.overline}</p>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight mb-12">{t.testimonials.title}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {t.testimonials.items.map((tm, i) => (
            <div key={i} data-testid={`testimonial-${i}`} className="bg-card border border-white/10 rounded-lg p-8 relative">
              <Quote className="h-8 w-8 text-muted-foreground/40 mb-4" />
              <p className="text-base font-light leading-relaxed mb-6">“{tm.text}”</p>
              <div className="flex items-center justify-between">
                <span className="font-display text-lg">{tm.name}</span>
                <span className="flex gap-0.5">{Array(5).fill(0).map((_, k) => <Star key={k} className="h-4 w-4 fill-foreground text-foreground" />)}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CONTACT */}
      <section id="contacto" className="px-6 md:px-12 lg:px-24 py-20 md:py-32 bg-secondary/30 border-t border-white/10">
        <div className="max-w-3xl">
          <p className="text-overline mb-3">{t.contact.overline}</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight mb-6">{t.contact.title}</h2>
          <p className="text-muted-foreground font-light mb-10 leading-relaxed">{t.contact.text}</p>
          <div className="flex flex-wrap gap-8 mb-10 text-sm">
            <span className="flex items-center gap-2 text-muted-foreground"><MapPin className="h-4 w-4" /> {BRAND.city}, {BRAND.country}</span>
            <span className="flex items-center gap-2 text-muted-foreground"><Mail className="h-4 w-4" /> {BRAND.email}</span>
            <span className="flex items-center gap-2 text-muted-foreground"><MessageCircle className="h-4 w-4" /> {BRAND.whatsappDisplay}</span>
          </div>
          <a href={`https://wa.me/${BRAND.whatsapp}`} target="_blank" rel="noreferrer">
            <Button data-testid="contact-whatsapp-button" className="h-12 px-8 bg-[#25D366] hover:bg-[#1faa52] text-black font-semibold uppercase tracking-wide gap-2">
              <MessageCircle className="h-5 w-5" /> {t.contact.button}
            </Button>
          </a>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="px-6 md:px-12 lg:px-24 py-12 border-t border-white/10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <span className="font-display text-lg">Valientes <span className="italic text-muted-foreground">Cenizas</span> Shop</span>
          <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} {BRAND.name}. {t.footer.rights}</p>
          <a href="/admin/login" className="text-xs text-muted-foreground/60 hover:text-muted-foreground" data-testid="admin-link">{t.nav.admin}</a>
        </div>
      </footer>
    </div>
  );
}
